import React, { createRef, memo, useState } from 'react';
import { Header } from '../../components/layout/header';
import Search from 'antd/es/input/Search';
import { Button, Dropdown, message, Modal, Segmented } from 'antd';
import { useDebounce } from '../../hook/use-debounce';
import { BrandApi } from '../../apis';
import { Table, TableRef } from '../../components/table/table';
import { ExclamationCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { BrandForm, BrandFormRef } from '../../components/brand/form';
import { PermissionCode, useRole } from '../../hook/use-role';

const PAGE_NAME = PermissionCode.CAR_BRAND;

const BrandListScreen = memo(() => {
  const [value, setValue] = useState<string | undefined>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [action, setAction] = useState<any>();
  const [sourceType, setSourceType] = useState<any>(null);

  const { check } = useRole();

  const [messageApi, contextHolderMessage] = message.useMessage();

  const formRef = createRef<BrandFormRef | null>();
  const tableRef = createRef<TableRef>();

  const [modal, contextHolder] = Modal.useModal();

  const query = useDebounce(value, 300);

  const column = columns({
    options: {
      isDelete: check.isDelete(PAGE_NAME),
      isUpdate: check.isEdit(PAGE_NAME),
    },
    setType: async (type: 'delete' | 'update', id: string) => {
      console.log('type :', tableRef.current);

      switch (type) {
        case 'update':
          const res = await BrandApi.get(id);
          setAction(res);
          setIsModalOpen(true);
          break;
        case 'delete':
          modal.confirm({
            title: 'Устгах',
            icon: <ExclamationCircleOutlined />,
            content: 'Устгахдаа итгэлтэй байна уу!',
            okText: 'Тийм',
            cancelText: 'Үгүй',
            onOk: async () => {
              await BrandApi.remove(id);
              tableRef.current?.reload();
              messageApi.success('Амжилттай устлаа');
            },
          });
          break;
      }
    },
  });

  const onSearch = (value: any) => {
    setValue(value.target.value);
  };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = async () => {
    const res = await formRef.current?.submitForm();
    if (res) {
      onSubmit(res);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setAction(undefined);
  };

  const onSubmit = async (values: any) => {
    try {
      if (action) {
        await BrandApi.update(action.id, values);

        setIsModalOpen(false);
        setAction(undefined);
        tableRef.current?.reload();

        messageApi.success('Амжилттай засварлалаа');
      } else {
        await BrandApi.create(values);

        setIsModalOpen(false);

        tableRef.current?.reload();
        messageApi.success('Амжилттай');
      }
    } catch (err: any) {
      messageApi.error(err?.message);
    }
  };

  return (
    <>
      {contextHolderMessage}
      <div className="flex-col bg-white rounded-[8px] p-5">
        <Header title="Брэнд" isGoBack={false} />
        <div className="">
          <div className="flex flex-row justify-between pb-[20px]">
            <div className="flex flex-row">
              <div className="flex flex-row">
                <Search placeholder="Хайх..." onChange={onSearch} style={{ width: 300 }} />
              </div>
              <div className="w-[10px]" />
              <Segmented
                value={sourceType}
                options={[
                  { label: 'БҮГД', value: null },
                  { label: 'UNEGUI', value: 'UNEGUI' },
                  { label: 'SMARTCAR', value: 'SMARTCAR' },
                  { label: 'INTERNAL', value: 'INTERNAL' },
                ]}
                onChange={(value) => {
                  setSourceType(value);
                }}
              />

              <div className="w-[10px]" />
              <Button type="default">Цэвэрлэх</Button>
            </div>

            {check.isCreate(PAGE_NAME) && (
              <Button type="primary" onClick={showModal} icon={<PlusCircleOutlined />}>
                Нэмэх
              </Button>
            )}
          </div>
          <Table
            ref={tableRef}
            columns={column}
            loadData={BrandApi.list}
            filter={{
              source: sourceType,
            }}
            query={query}
          />
        </div>
      </div>

      {isModalOpen && (
        <Modal
          title="Брэнд нэмэх"
          closable={{ 'aria-label': 'Custom Close Button' }}
          open={isModalOpen}
          onOk={handleOk}
          onCancel={handleCancel}
          okText="Нэмэх"
          cancelText="Буцах"
          maskClosable={false}
        >
          <BrandForm action={action} ref={formRef} onSubmit={onSubmit} />
        </Modal>
      )}

      {contextHolder}
    </>
  );
});

const columns: any = ({
  options,
  setType,
}: {
  setType: (type: 'delete' | 'update', id: string) => void;
  options: {
    isDelete: boolean;
    isUpdate: boolean;
  };
}) => [
  {
    title: '#',
    key: 'rowIndex',
    width: 70,
    align: 'center',
    render: (_: any, __: any, index: any) => index + 1,
  },
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
  },
  {
    title: 'Source',
    dataIndex: 'source',
    key: 'name',
  },
  {
    title: 'Source',
    key: 'name',
    render: (item: any, _: any, index: any) => <label>{item.sourceUrl}</label>,
  },
  {
    key: 'rowIndex',
    width: 150,
    align: 'center',
    render: (item: any, _: any, index: any) => {
      if (!options?.isDelete && !options?.isUpdate) {
        return <></>;
      }

      let menus = [];

      if (options?.isUpdate === true) {
        menus.push({
          key: 'update',
          label: <span>Засах</span>,
          onClick: () => {
            console.log('item :', item);
            setType('update', item?.id!);
          },
        });
      }

      if (options?.isDelete === true) {
        menus.push({
          key: 'delete',
          label: <span className="text-red-500">Устгах</span>,
          onClick: () => setType('delete', item?.id!),
        });
      }

      return (
        <Dropdown
          trigger={['click']}
          menu={{
            items: menus,
          }}
          arrow
          placement="bottomLeft"
        >
          <Button type="default">Үйлдэл</Button>
        </Dropdown>
      );
    },
  },
];

BrandListScreen.displayName = 'BrandListScreen';

export { BrandListScreen };
